import { useTranslation } from "react-i18next";
import useSwr, { mutate } from "swr";
import { apiClient, fetcher, SWR_KEYS } from "../lib/api";
import type { AnyService, ServiceRequest } from "../types";

interface ServicesResponse {
  services: AnyService[];
  total?: number;
}

interface ServiceRequestsResponse {
  requests: ServiceRequest[];
}

interface HiddenServicesResponse {
  hidden: Array<{
    id: string;
    serviceId: string;
    serviceType: "default" | "custom";
    name: string;
    provider: string;
    isHidden: boolean;
  }>;
}

// 継承システム対応のサービスフック（多言語対応）- API v1
export const useServices = () => {
  const { i18n } = useTranslation();
  const locale = (i18n.language === "ja" ? "ja" : "en") as "en" | "ja";

  const {
    data,
    error,
    isLoading,
    mutate: revalidate,
  } = useSwr<ServicesResponse>(SWR_KEYS.SERVICES_V1(locale), fetcher);

  const updateServiceOptimistically = (serviceId: string, updates: Partial<AnyService>) => {
    if (!data) return;

    const updatedServices = data.services.map((service) =>
      service.id === serviceId
        ? {
            ...service,
            ...updates,
            // customUpdatedAt / isCustomized は ManagedService のみ
            ...(service.serviceType !== "user_owned" && {
              customUpdatedAt: new Date().toISOString(),
              isCustomized: true,
            }),
          }
        : service,
    );

    // 楽観的更新（サービス一覧のみ、再検証なし）- API v1
    mutate(SWR_KEYS.SERVICES_V1(locale), { services: updatedServices }, false);
  };

  /**
   * お気に入り一覧（ホームページの My Deck）を楽観的に更新する
   * - 解除: 該当サービスを除去
   * - 追加: サービス一覧の同オブジェクトを末尾に追加（正しい並び順は後続の再検証で反映）
   * - キャッシュ未ロード時は何もしない（undefined を書き込んで空状態を描画させない）
   */
  const updateFavoritesOptimistically = (service: AnyService, isFavorite: boolean) => {
    mutate<{ services: AnyService[] }>(
      SWR_KEYS.SERVICES_V1_FAVORITES(locale),
      (current) => {
        if (!current) return current;
        const others = current.services.filter((s) => s.id !== service.id);
        return { services: isFavorite ? [...others, { ...service, isFavorite: true }] : others };
      },
      false,
    );
  };

  const toggleFavorite = async (serviceId: string) => {
    try {
      const service = data?.services.find((s) => s.id === serviceId);
      if (!service) return;

      const newFavoriteStatus = !service.isFavorite;

      // 楽観的更新（サービス一覧とお気に入り一覧の両方）
      updateServiceOptimistically(serviceId, { isFavorite: newFavoriteStatus });
      updateFavoritesOptimistically(service, newFavoriteStatus);

      // API v1を使用（全サービスタイプに対応: default, custom, user_owned）
      // toggleFavoriteV1 は user_service_states テーブルを更新する（統一API）
      // serviceType は service.serviceType から取得（API v1対応済み）
      const serviceType = service.serviceType;
      await apiClient.toggleFavoriteV1(serviceType, serviceId, newFavoriteStatus);

      // データを再取得（サービス一覧とお気に入り一覧の両方）- API v1
      // 注: mutate は 1 引数形で呼ぶ。第 2 引数に undefined を渡すとキャッシュが消えて一瞬空状態が描画される
      await Promise.all([
        revalidate(),
        mutate(SWR_KEYS.FAVORITES_V1),
        mutate(SWR_KEYS.SERVICES_V1_FAVORITES(locale)),
      ]);
    } catch (error) {
      // エラーが発生した場合は元に戻す
      revalidate();
      mutate(SWR_KEYS.FAVORITES_V1);
      mutate(SWR_KEYS.SERVICES_V1_FAVORITES(locale));
      throw error;
    }
  };

  const hideService = async (serviceId: string) => {
    try {
      const service = data?.services.find((s) => s.id === serviceId);
      if (!service) return;

      // user_owned サービスは非表示ではなく削除を使用する
      if (service.serviceType === "user_owned") {
        throw new Error("ユーザー独自サービスは非表示にできません。削除を使用してください。");
      }

      // 楽観的更新（UIから非表示）
      updateServiceOptimistically(serviceId, { isHidden: true });

      // serviceType決定（フォールバック付き）
      const serviceType = service.serviceType;

      // API v1呼び出し（serviceType対応）
      await apiClient.setHiddenV1(serviceType, serviceId, true);

      // データを再取得
      await Promise.all([revalidate(), mutate(SWR_KEYS.HIDDEN_SERVICES_V1)]);
    } catch (error) {
      revalidate();
      throw error;
    }
  };

  const restoreService = async (
    serviceId: string,
    serviceType: "default" | "custom" | "user_owned" = "default",
  ) => {
    // API v1呼び出し（serviceType対応）
    await apiClient.setHiddenV1(serviceType, serviceId, false);

    // データを再取得 - API v1
    await Promise.all([revalidate(), mutate(SWR_KEYS.HIDDEN_SERVICES_V1)]);
  };

  const updateServiceOverride = async (
    serviceId: string,
    overrideData: {
      customName?: string;
      customDescription?: string;
      customPricing?: string;
      personalNotes?: string | null; // userPersonalNotes に対応
    },
    serviceType: "default" | "custom" = "default",
  ) => {
    try {
      // 楽観的更新（userPersonalNotes にマッピング）
      updateServiceOptimistically(serviceId, {
        customName: overrideData.customName,
        customDescription: overrideData.customDescription,
        customPricing: overrideData.customPricing,
        userPersonalNotes: overrideData.personalNotes,
      } as Partial<AnyService>);

      // API v1を使用
      await apiClient.updateServiceInfoV1(serviceType, serviceId, {
        customName: overrideData.customName,
        customDescription: overrideData.customDescription,
        personalNotes: overrideData.personalNotes,
      });
      revalidate();
    } catch (error) {
      revalidate();
      throw error;
    }
  };

  const resetToDefault = async (
    serviceId: string,
    serviceType: "default" | "custom" | "user_owned" = "default",
  ) => {
    // API v1を使用
    await apiClient.resetServiceStateV1(serviceType, serviceId);
    revalidate();
  };

  return {
    services: data?.services || [],
    error,
    isLoading,
    revalidate,
    updateServiceOptimistically,
    toggleFavorite,
    hideService,
    restoreService,
    updateServiceOverride,
    resetToDefault,
  };
};

// お気に入りサービスフック - API v1
// API v1の /api/v1/services?favoritesOnly=true を使用（フルサービス情報を返却）
export const useFavoriteServices = () => {
  const { i18n } = useTranslation();
  const locale = (i18n.language === "ja" ? "ja" : "en") as "en" | "ja";

  const {
    data,
    error,
    isLoading,
    mutate: revalidate,
  } = useSwr<{ services: AnyService[] }>(SWR_KEYS.SERVICES_V1_FAVORITES(locale), fetcher);

  return {
    favoriteServices: data?.services || [],
    error,
    isLoading,
    revalidate,
  };
};

// 非表示サービスフック - API v1
export const useHiddenServices = () => {
  const {
    data,
    error,
    isLoading,
    mutate: revalidate,
  } = useSwr<HiddenServicesResponse>(SWR_KEYS.HIDDEN_SERVICES_V1, fetcher);

  return {
    hiddenServices: data?.hidden || [],
    error,
    isLoading,
    revalidate,
  };
};

// サービス申請フック
export const useServiceRequests = () => {
  const {
    data,
    error,
    isLoading,
    mutate: revalidate,
  } = useSwr<ServiceRequestsResponse>(SWR_KEYS.SERVICE_REQUESTS, fetcher);

  const submitRequest = async (requestData: {
    serviceName: string;
    serviceUrl: string;
    description: string;
    reasoning: string;
    categorySuggestion?: string;
  }) => {
    await apiClient.submitServiceRequest(requestData);
    revalidate(); // 申請一覧を再取得
  };

  return {
    requests: data?.requests || [],
    error,
    isLoading,
    revalidate,
    submitRequest,
  };
};
